// Copyright (c) Ping. All rights reserved.
//
// Process-wide set of conversation ids that are per-call MLS groups. The
// callee's join-via-Welcome path stores the group with NO name (the
// `createConversation({name:"call:…"})` label is local to the creator), so the
// name-prefix check can't identify a call group on the callee. Instead the call
// layer registers `call_group_id` here as soon as it learns it from the
// `call_invite` (or on create), and `useIncomingConversations` skips any
// registered id — so call groups never surface as chat rows on any device.
//
// DURABILITY: the in-memory set alone is not enough. It resets on every reload,
// so after a refresh the callee no longer knows a group is a call group and the
// SDK re-emitting it would re-persist a ghost "New chat" row. The host wires a
// persistence hook (`setCallGroupPersistHook`) so registrations are written to a
// durable store and re-hydrated on boot (`hydrateCallGroups`), and so a group
// that already leaked into the chat list can be purged the instant it's
// recognised as a call group (`onRegister`).

const callGroupIds = new Set<string>();

/** Host-supplied side effect run once per NEW registration (persist + purge). */
export type CallGroupRegisterHook = (idHex: string) => void;

let persistHook: CallGroupRegisterHook | null = null;

/** Wire durable persistence + leaked-row purge. Pass `null` to detach. */
export function setCallGroupPersistHook(hook: CallGroupRegisterHook | null): void {
  persistHook = hook;
}

/** Seed the in-memory set from the durable store on boot (no hook re-fire). */
export function hydrateCallGroups(idsHex: readonly string[]): void {
  for (const id of idsHex) {
    if (id) callGroupIds.add(id.toLowerCase());
  }
}

export function registerCallGroup(idHex: string): void {
  if (!idHex) return;
  const id = idHex.toLowerCase();
  const isNew = !callGroupIds.has(id);
  callGroupIds.add(id);
  // Run the durable-persist + purge side effect only on first sight so a
  // per-call re-register (invite + accept + signaling all suppress) is cheap.
  if (isNew && persistHook !== null) persistHook(id);
}

export function unregisterCallGroup(idHex: string): void {
  callGroupIds.delete(idHex.toLowerCase());
}

/**
 * Tear the registry down on session end (logout / account switch). Clears the
 * in-memory id set AND unwires the persist hook, so no call-group write can fire
 * during or after a local wipe (which would resurrect purged content, or hold an
 * IDB handle open against the teardown) and so a durable id set never bleeds
 * across accounts on a shared browser. `initCallGroupPersistence` re-wires +
 * re-hydrates on the next sign-in.
 */
export function resetCallGroups(): void {
  callGroupIds.clear();
  persistHook = null;
}

export function isRegisteredCallGroup(idHex: string): boolean {
  return callGroupIds.has(idHex.toLowerCase());
}

/** Every registered call-group id (lowercase hex) — for durable persistence. */
export function allCallGroupIds(): readonly string[] {
  return [...callGroupIds];
}
